import React, {
  ComponentProps,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { IonicInput } from 'ionic-ui';
import { caretDown } from 'ionicons/icons';
import { IonIcon } from '@ionic/react';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { Countries, Country } from '../../data/Countries';
import getCountryCode from '../../utils/helperFunction/getCountryCode';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { IonicContextMenu } from './ContextMenu';

type Props = Omit<ComponentProps<typeof IonicInput>, 'prefix' | 'onChange'> & {
  onCountryChange?: (countryIndex: number) => void;
  onChange?: (value: string) => void;
};

// COUNTRIES MENU TRIGGER
type ContextTriggerProps = {
  isOpen?: boolean;
  userCountryIndex?: number;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;
const ContextTrigger = React.forwardRef<HTMLButtonElement, any>(
  ({ isOpen, userCountryIndex, ...props }: ContextTriggerProps, ref) => (
    <button
      ref={ref}
      type="button"
      className="flex items-center gap-2"
      {...props}
    >
      <span
        className={`fi fi-${Countries[userCountryIndex || 0].code.toLowerCase()}`}
      ></span>
      <IonIcon
        icon={caretDown}
        className={`${isOpen ? 'rotate-180' : 'rotate-0'} transition-all  medium-emphasis-text`}
      />
    </button>
  )
);

// COUNTRIES MENU
const InputMenu = memo(
  ({
    onCountryChange,
    userCountryIndex,
    variant,
    size,
  }: {
    onCountryChange: (idx: number) => void;
    userCountryIndex: number;
  } & Pick<ComponentProps<typeof IonicContextMenu>, 'size' | 'variant'>) => {
    const countryOptions = useMemo(() => {
      return Countries.map((cc, ci) => ({
        label: `<span class="fi fi-${cc.code.toLowerCase()}"></span> &nbsp; ${cc.name} <span className="px-2 medium-emphasis-text">${cc.dial_code}</span>`,
        onClick: () => onCountryChange(ci),
      }));
    }, []);

    return (
      <IonicContextMenu
        variant={variant}
        size={size}
        trigger={<ContextTrigger userCountryIndex={userCountryIndex} />}
        options={countryOptions}
      />
    );
  },
  (prev, next) => prev.userCountryIndex === next.userCountryIndex
);

//MAIN PHONE NUMBER COMPONENT
const PhoneNumberInput = forwardRef<HTMLInputElement, Props>(function (
  { onCountryChange, value = '', onChange, ...props }: Props,
  ref
) {
  const [error, setError] = useState('');
  const [countryIndex, setCountryIndex] = useState<number>(0);

  const { timezone: userTz } = useSelector(
    (state: RootState) => state.settings
  );

  const handleCountryChange = useCallback((index: number) => {
    setCountryIndex(index);
    if (onCountryChange) onCountryChange(index);
  }, []);

  const parsePhoneNo = (phoneNumber: string, country?: Country) => {
    const parsed = parsePhoneNumberFromString(
      phoneNumber,
      (country?.code as CountryCode) || undefined
    );

    return parsed;
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const selectedCountry = Countries[countryIndex];
    const parsed = parsePhoneNo(rawValue, selectedCountry);

    if (rawValue.trim() === '') {
      setError('');
      onChange?.('');
      return;
    }

    if (!parsed || !parsed.isValid()) {
      setError('Invalid phone number');
      onChange?.(rawValue);
    } else {
      setError('');
      onChange?.(parsed.number); // E.164 format e.g. +2349081885935
    }
  };

  useEffect(() => {
    const countryCodeIndex = getCountryCode(userTz);
    handleCountryChange(countryCodeIndex);
  }, [userTz]);

  useEffect(() => {
    if (!value) return;

    const parsed = parsePhoneNo(value as string);

    if (parsed?.country) {
      const index = Countries.findIndex(
        (c) => c.code.toUpperCase() === parsed.country
      );

      if (index !== -1) {
        setCountryIndex(index);
        onCountryChange?.(index);
      }
    }
  }, []);

  return (
    <IonicInput
      {...props}
      ref={ref}
      type="tel"
      value={value}
      onChange={handleValueChange}
      prefix={
        <InputMenu
          variant={props.variant as any}
          userCountryIndex={countryIndex}
          size={props.size as any}
          onCountryChange={handleCountryChange}
        />
      }
      hint={error || props.hint}
      className={`${error ? 'border-red-500' : ''}`}
      containerClass="relative"
    />
  );
});

export default PhoneNumberInput;
